/* ============================================================
   utils — small pure helpers ported from prototype/reader.html
   (hashing, color, time formatting, file-type test).
   ============================================================ */

export async function sha256Hex(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text)
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// Author/tab hues live on an abstract 0–360 wheel (max-spaced by farthestHue).
// We RENDER them through a curated arc that deliberately excludes the two
// reserved bands of the 3-color discipline — RED (the caret mark) and YELLOW
// (highlights) — so an author dot/rail/highlight can never be mistaken for
// either. The arc runs green→teal→cyan→blue→violet→magenta (the cool half),
// in oklch to match the rest of the theme palette (everything else is oklch).
const SAFE_HUE_LO = 160 // green, clear of yellow (~100)
const SAFE_HUE_SPAN = 150 // → 310 (violet): green→teal→cyan→blue→indigo→violet,
// stopping before magenta/pink so no author hue ever reads warm/red-adjacent.

/** Color for an abstract hue (0–360), mapped onto the reserved-band-free arc
 *  and rendered in oklch with light/dark-adapted lightness/chroma. Shared so the
 *  pinned Welcome/Docs tabs sit in the same color language as the doc tabs and
 *  the reviewer author colors. */
export function hueColor(hue: number): string {
  const dark = document.documentElement.dataset.mode === 'dark'
  const h = SAFE_HUE_LO + ((((hue % 360) + 360) % 360) / 360) * SAFE_HUE_SPAN
  const l = dark ? 0.72 : 0.6
  const c = dark ? 0.13 : 0.15
  return `oklch(${l} ${c} ${h.toFixed(1)})`
}

// Author/tab colors snap to an evenly-spaced hue lattice rather than the raw
// `hash % 360`. A continuous mapping lets two random ids land a few degrees
// apart and read as the same color; snapping to N slots guarantees any two
// *distinct* slots are 360/N apart (clearly different), at the cost of an
// occasional exact collision (1/N). Stable per id (same id → same hue
// everywhere) and stateless — colors never shift as other authors join.
const HUE_SLOTS = 12
// Nudge off pure red (0°) so the first slot isn't an alarm color.
const HUE_OFFSET = 14
export function hashColor(hash: string): string {
  const slot = parseInt(hash.slice(0, 8), 16) % HUE_SLOTS
  return hueColor(Math.round((slot * (360 / HUE_SLOTS) + HUE_OFFSET) % 360))
}

/**
 * Pick the hue that is *maximally distinct* from a set of already-used hues:
 * the midpoint of the largest gap around the 360° wheel. Used to assign reviewer
 * colors so people stay as far apart as possible — 1 author → 14°, 2 → 180°
 * apart, 3 → 120° apart, and so on, the gaps only shrinking once there are many
 * reviewers. Deterministic (pure function of the input set), so every client
 * computes the same color for the same author. Empty set → the base offset.
 */
export function farthestHue(hues: number[]): number {
  if (hues.length === 0) return HUE_OFFSET
  const sorted = [...hues].map((h) => ((h % 360) + 360) % 360).sort((a, b) => a - b)
  let bestStart = sorted[0]
  let bestGap = -1
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]
    // Wrap the last gap around to the first hue + 360.
    const next = i + 1 < sorted.length ? sorted[i + 1] : sorted[0] + 360
    const gap = next - cur
    if (gap > bestGap) {
      bestGap = gap
      bestStart = cur
    }
  }
  return Math.round((bestStart + bestGap / 2) % 360)
}

export function relativeTime(ms: number): string {
  const dt = Date.now() - ms
  const m = Math.floor(dt / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return m + 'm ago'
  const h = Math.floor(m / 60)
  if (h < 24) return h + 'h ago'
  const d = Math.floor(h / 24)
  if (d === 1) return 'yesterday'
  if (d < 30) return d + 'd ago'
  const mo = Math.floor(d / 30)
  return mo + 'mo ago'
}

export function formatAbsoluteDate(ms: number): string {
  const d = new Date(ms)
  const now = new Date()
  const sameYear = d.getFullYear() === now.getFullYear()
  const opts: Intl.DateTimeFormatOptions = sameYear
    ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { year: 'numeric', month: 'short', day: 'numeric' }
  return d.toLocaleString(undefined, opts)
}

/**
 * The plan's de-facto title: the text of its first ATX heading (`# …`),
 * skipping fenced ``` / ~~~ code blocks so a `#` comment inside a snippet is
 * never mistaken for a heading. Strips the leading `#`s and any trailing `#`s
 * (closed ATX). Returns `null` when the markdown has no heading.
 */
export function deriveTitle(markdown: string): string | null {
  let fence: string | null = null
  for (const raw of markdown.split('\n')) {
    const line = raw.trim()
    const fenceMatch = line.match(/^(```+|~~~+)/)
    if (fence) {
      // Inside a fence: only a matching (or longer) closing fence ends it.
      if (fenceMatch && line.startsWith(fence)) fence = null
      continue
    }
    if (fenceMatch) {
      fence = fenceMatch[1]
      continue
    }
    const heading = line.match(/^#{1,6}\s+(.*)$/)
    if (heading) {
      const text = heading[1].replace(/\s+#+\s*$/, '').trim()
      if (text) return text
    }
  }
  return null
}

export function isMarkdownFile(file: File): boolean {
  if (/\.(md|markdown)$/i.test(file.name)) return true
  if (file.type === 'text/markdown') return true
  return false
}
